import * as React       from "react";
import {cx}             from "emotion";

import {XdSentry}       from "@Common/libs/sentry/xd-sentry";
import {Types}          from "@Common/types";
import {ComponentLink,
        Link,
        makeChain}      from "@Common/react/cst2";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";
import {UseRef}         from "@Common/react/use-ref";
import {UseState}       from "@Common/react/use-state";
import {CcDirectApis3}  from "@Common/tools/cc-integration/cc_apis/cc-direct-apis-3";
import {ConfirmDialog}  from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {UploadButton}   from "@Common/ui-components/upload-button/upload-button";

import {ErrorMessage}      from "../components/error-message";
import {LoadingSpinner}    from "../components/loading-spinner";
import {GalleryImage}      from "../components/gallery-image";
import {MultiSelectFooter} from "../components/multi-select-footer";
import {Paginator}         from "../components/paginator";
import {GetImageQuality}   from "../utils/get-image-quality";


export const TabCustomersCanvasGallery = ({
    modal,
    folderName,
    viewType,
    numResultsPerPage,
    imageArea             = null,
    singleSelectOptions   = null,
    multiSelectOptions    = null,
    isMobile,
    disallowSvgImages     = false,
    onPaginateNumResults,
}:{
/*  PROP TYPES  *******************************************************************************************************/
    modal                 : Types.Common.UiComponents.ModalService3.ModalService3Next<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>,
    folderName            : string,
    viewType              : Types.Common.UiComponents.PhotoDashboard.viewTypes,
    numResultsPerPage     : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    imageArea            ?: Types.Common.UiComponents.PhotoDashboard.Models.ImageArea,
    singleSelectOptions  ?: Types.Common.UiComponents.PhotoDashboard.Models.SingleSelectOptions,
    multiSelectOptions   ?: Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectOptions,
    isMobile              : boolean,
    disallowSvgImages    ?: boolean,
    onPaginateNumResults  : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults,
}) => (
    /*  Notice: CC galleries offer a system of folders, which can be read and containing images can be displayed for
        viewing. These cannot be applied to an image area on the design though. This may be caused by a bug in the CC
        designer or APIs, which could be resolved in a later version (last tested with version 5.22). If folder support
        is wanted at a later date, can use the getImageGalleryCategories method in CcDirectApis3 to test.
    */
    makeChain<JSX.Element>()

    .then(...consumeContext("ccApis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("systemConstants"))

    // build the API interface
    .then("ccInternalToken", ({ccApis}, next:ComponentLink<typeof Load2>) => Load2({
        fetch: () => ccApis.getToken(),
        next
    }))

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        imagesPerPage           :number,
        imagesCurrentPage       :number,
        isImageLoadingToEditor  : boolean,
        uploadProgressPercent   : number,
        editingImageId          : number,
        showFooterUpload        : boolean,
        errorMessage            : string,
        pageNumber              : number,
    }>>) => UseState({
        defaultValue: {
            // pagination parameters are passed to the CC API, but will there are problems with the API not working
            // to their spec, such as ordering not working, and no total number of images/pages outside of the current
            // view
            imagesPerPage: 100, // max 100
            imagesCurrentPage: 1,
            // the CC API sort parameters do not work as specified in their documentation, so leaving out for now - this
            // may be fixed in CC6
            //imageSortOrder: "Name",
            //imageSortDescending: false,
            isImageLoadingToEditor: false,
            uploadProgressPercent: 0,
            editingImageId: 0,
            showFooterUpload: false,
            errorMessage: "",
            pageNumber: 1,
        },
        next
    }))

    // display loader while applying images to the editor
    .then(({state}, next:Link<JSX.Element>) => {
        if (state.value.isImageLoadingToEditor) {
            return (
                <div className="tab-customers-canvas-gallery-pta tab-content customers-canvas-gallery">
                    <div className="results">
                        <LoadingSpinner text="Applying Image"/>
                    </div>
                </div>
            );
        }

        return next();
    })

    .then(({ccInternalToken}, next:Link<JSX.Element>) => {
        if (ccInternalToken.loading) {
            return (
                <div className="tab-content customers-canvas-gallery">
                    <div className="results">
                        <LoadingSpinner text="Loading API"/>
                    </div>
                </div>
            );
        }

        return next();
    })

    // build the API interface
    .then("ccDirectApi", ({ccApis, ccInternalToken}, next:ComponentLink<typeof Load2>) => {
        return next(CcDirectApis3({
            host: ccApis.currentHost,
            userId: ccInternalToken.value.userGUID,
            tokenId: ccInternalToken.value.tokenId}));
    })

    // load photos
    .then("loaderPhotos", ({ccDirectApi, state}, next:ComponentLink<typeof Load2>) => Load2({
        _key: folderName,
        fetch: () => ccDirectApi.getImageGalleryImages(
            folderName,
            state.value.imagesPerPage,
            state.value.imagesCurrentPage,
        ),
        captureException: true,
        next,
    }))

    // evaluate the max file size - single page max for external users or PHP limit for internal users
    .then("maxFileSize", ({userLevels, systemConstants}, next:Link<JSX.Element, number>) => next(
        (!userLevels?.internalUser) ? systemConstants.value.UPLOAD_MAX_PER_PAGE : systemConstants.value.UPLOAD_MAX_PHP_FILE_SIZE
    ))

    // build the upload method
    .then("onUpload", ({ccApis, ccInternalToken, company, state, loaderPhotos, maxFileSize}, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.Functions.OnUpload>) => next(async (event) => {

        // clear the error message if one is set
        if (state.value.errorMessage) state.onChange({...state.value, errorMessage: ""});

        // issue error if problem capturing the file
        if (!event.target || !event.target.files.length || Array.from(event.target.files).find(x => !x.size)) {
            state.onChange({
                ...state.value,
                errorMessage: "There was an error applying your image selection. Please try again.",
            });
            return;
        }

        // validate all files
        let allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
        if (!disallowSvgImages) allowedTypes.push("image/svg+xml");
        for (const file of event.target.files) {
            if (!allowedTypes.includes(file.type)) {
                const validationMessageImageName = (event.target.files.length > 1) ? `“${file.name}”` : "";
                state.onChange({
                    ...state.value,
                    errorMessage: `Your ${validationMessageImageName} is not supported. Please select a .jpg, .png${disallowSvgImages ? "" : ", .svg"} or .pdf file.`,
                });
                return;
            }
            if (file.size > maxFileSize) {
                const validationMessageImageName = (event.target.files.length > 1) ? `“${file.name}”` : "";
                state.onChange({
                    ...state.value,
                    errorMessage: `Your image ${validationMessageImageName} is too large. Please select an image less than ${Math.round(maxFileSize/1000000)}MB.`,
                });
                return;
            }
        }

        // find total payload to be uploaded for calculating progress percent
        let totalPayload = Array.from(event.target.files).reduce((total, file) => total + file.size, 0);
        let uploadedPayload = 0;

        // initialize variables used in process for warning of images skipped due to low quality settings
        const numFiles = event.target.files.length; // needed because event.target won't exist when the final error message is constructed
        let lowQualityFiles = [];

        // upload all files
        for (const file of event.target.files) {
            // if disabled low quality, skip upload of low quality photos
            if (!company.prefs.allowLowQualityPhotosProperty
                && GetImageQuality({
                    imageInfo: {
                        fileSize: file.size,
                        fileType: (file.type === "image/png") ? "png" : "",
                    },
                    photoQualitySettings: imageArea.photoQualitySettings
                }) === "Low"
            ) {
                lowQualityFiles.push(file.name);
                totalPayload -= file.size;
                continue;
            }

            // upload the file
            let uploadedFile;
            try {
                uploadedFile = await ccApis.uploadImageToGallery({
                    userId: ccInternalToken.value.userGUID,
                    tokenId: ccInternalToken.value.tokenId,
                    folderName,
                    image: file
                });
            } catch (e) {}
            // issue an error if no response
            if (!uploadedFile) {
                XdSentry.addBreadcrumb({
                    category: "custcanv",
                    message: "User attempted to upload an image that failed during transmission.",
                    level: XdSentry.Severity.Error,
                    data: {
                        folderName,
                        uploadFileData: {file},
                    }
                });
                XdSentry.captureMessage("Image Upload Error", XdSentry.Severity.Log);
                state.onChange({
                    ...state.value,
                    errorMessage: "There was an error uploading your file.",
                });
                return;
            }

            // issue error if no path was returned
            if (!uploadedFile.originalUrl) {
                XdSentry.addBreadcrumb({
                    category: "custcanv",
                    message: "User uploaded an image that that did not return a print URL.",
                    level: XdSentry.Severity.Error,
                    data: {
                        uploadFileData: file,
                        returnedData: uploadedFile,
                    }
                });
                XdSentry.captureMessage("Image Upload Error", XdSentry.Severity.Log);
                state.onChange({
                    ...state.value,
                    errorMessage: "There was an error uploading your file.",
                });
                return;
            }

            // add size to payload for calculating upload percent
            uploadedPayload += file.size;

            // auto-select the image if using multi-select
            if (multiSelectOptions) {
                multiSelectOptions.onSelect({
                    imageId: uploadedFile.id,
                    assetType: "user-created",
                    imagePath: image.originalUrl + "?X-CCToken=" + ccInternalToken.value.tokenId,
                    thumbnailUrl: image.originalUrl + "?X-CCToken=" + ccInternalToken.value.tokenId,
                });
            }

        }

        // construct an error message for any low quality images that were skipped due to company quality rules
        let errorMessage = "";
        if (lowQualityFiles.length) {
            if (numFiles === 1)
                errorMessage = "Your image was not uploaded because it does not meet the minimum quality standards for our printing process. Please select a larger image.";
            else if (numFiles === lowQualityFiles.length)
                errorMessage = "Your images were not uploaded because they do not meet the minimum quality standards for our printing process. Please select larger images.";
            else if (lowQualityFiles.length === 1)
                errorMessage = "The following file was not uploaded because it does not meet the minimum quality standards for our printing process: " + lowQualityFiles[0] + " Please select a larger image.";
            else
                errorMessage = "The following files were not uploaded because they do not meet the minimum quality standards for our printing process: " + lowQualityFiles.join(", ") + " Please select larger images.";
        }

        // reload the image results
        state.onChange({
            ...state.value,
            uploadProgressPercent: 0,
            errorMessage,
        });
        loaderPhotos.reload();

    }))
    .then("dialogConfirmDelete", (_, next:ComponentLink<typeof ConfirmDialog>) => ConfirmDialog({next}))

    // check if pagination is required
    .then("includePagination", ({loaderPhotos}, next:Link<JSX.Element, boolean>) => next(isMobile && !!loaderPhotos.value?.length))

    // render the images list
    .then("imagesContents", ({ccDirectApi, ccInternalToken, company, loaderPhotos, includePagination, dialogConfirmDelete,
        state}, next:Link<JSX.Element>) => {

        if (loaderPhotos.loading) return next(<LoadingSpinner text="Loading Photos"/>);

        // retrieve the image data from the loader
        let images = loaderPhotos.value.items;
        if (includePagination) images = images.slice((state.value.pageNumber-1)*numResultsPerPage, state.value.pageNumber*numResultsPerPage);

        // add a message for empty galleries
        if (!images.length) return next();

        // display the images when available
        return next(<>
            {images.map((image) => (
                <GalleryImage
                    key={image.storageId}
                    id={image.storageId}
                    imageName={image.name}
                    imageAttributionData={image.attribution}
                    viewType={viewType}
                    fileType={image.fileType}
                    fileSize={image.fileSize}
                    width={image.width}
                    height={image.height}
                    areaWidth={imageArea?.widthInches}
                    areaHeight={imageArea?.heightInches}
                    photoQualitySettings={imageArea?.photoQualitySettings}
                    allowLowQuality={company.prefs.allowLowQualityPhotosProperty}
                    disabled={state.value.uploadProgressPercent > 0 || (image.fileType === "svg" && disallowSvgImages)}
                    thumbnailUrl={image.deleteUrl + "?X-CCToken=" + ccInternalToken.value.tokenId}
                    selectedIndex={(!multiSelectOptions) ? 0 : multiSelectOptions.selectedImages.findIndex(x => x.assetType === "order" && x.imageId === image.id)+1}
                    onClick={
                        () => {
                           // apply the image for single select mode
                        if (singleSelectOptions) {

                            // set the image as applying
                            state.onChange({
                                ...state.value,
                                isImageLoadingToEditor: true,
                            });

                            // apply the image
                            singleSelectOptions.onApply(
                                imageArea,
                                "order",
                                0,
                                image.originalUrl + "?X-CCToken=" + ccInternalToken.value.tokenId, // image.path doesn't work for images within folders (not tested above CC v5.22)
                                modal.close
                            );

                        }

                        // select the image for multi-select mode
                        else if (multiSelectOptions) {

                            multiSelectOptions.onSelect({
                                assetType: "order",
                                imageId: 0,
                                imagePath: image.originalUrl + "?X-CCToken=" + ccInternalToken.value.tokenId,
                                thumbnailUrl: image.originalUrl + "?X-CCToken=" + ccInternalToken.value.tokenId
                            });

                        }
                        }
                    }
                    onDelete={
                        async () => {
                            // prompt delete confirmation
                            // @ts-ignore
                            if (await dialogConfirmDelete.show({
                                actionName: "delete",
                                message: <span>Deleting <i>{image.name}</i>.</span>,
                                buttons: {
                                    confirmLabel: "Yes",
                                },
                            })) {
                                await ccDirectApi.deleteImageGalleryImage(image.name, folderName);
                                loaderPhotos.reload();
                            }
                        }
                    }
                />
            ))}
        </>);
    })

    // create the results area ref hook for controlling scroll position
    .then("resultsAreaRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // create the upload button ref for checking if the button is out of view
    .then("uploadButtonRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // render the tab contents
    .then(({state, imagesContents, onUpload, loaderPhotos, maxFileSize, includePagination, resultsAreaRef, uploadButtonRef}) => (<>
        <div className={cx("tab-content customers-canvas-gallery", "viewtype-"+viewType)}>
            <div className="results"
                ref={ref => resultsAreaRef.set(ref)}
                onScroll={(e) => {

                    if (!uploadButtonRef.get()) return;
                    const topPosition = resultsAreaRef.get().scrollTop;
                    const uploadButtonPosition = uploadButtonRef.get().offsetTop + uploadButtonRef.get().offsetHeight;

                    if (!state.value.showFooterUpload && topPosition > uploadButtonPosition) {
                        state.onChange({...state.value, showFooterUpload: true});
                    }
                    else if (state.value.showFooterUpload && topPosition <= uploadButtonPosition) {
                        state.onChange({...state.value, showFooterUpload: false});
                    }

                }}
            >
                <ErrorMessage text={state.value.errorMessage}/>
                <div className="source-images">
                    {!loaderPhotos.loading && (
                    <div className="card upload">
                        <h2>Upload a Photo</h2>
                        <p className="browse">
                            <UploadButton
                                buttonRef={uploadButtonRef}
                                onUpload={onUpload}
                                uploadProgressPercent={state.value.uploadProgressPercent}
                                allowMultipleFiles={true}
                            />
                        </p>
                        <p>
                            Images must be JPEG (.jpg, .jpeg, .jpe), PNG{disallowSvgImages ? "" : ", SVG"} or PDF format. Less than {Math.round(maxFileSize/1000000)}MB in file size.
                        </p>
                    </div>
                    )}
                    {imagesContents}
                </div>
            </div>
            {includePagination && (
                <Paginator
                    numItems={loaderPhotos.value?.length}
                    pageNumber={state.value.pageNumber}
                    itemsPerPage={numResultsPerPage}
                    onPaginateNumResults={numResults => {
                        resultsAreaRef.get().scrollTop = 0;
                        onPaginateNumResults(numResults);
                    }}
                    onPaginatePageNumber={pageNumber => {
                        resultsAreaRef.get().scrollTop = 0;
                        state.onChange({...state.value, pageNumber});
                    }}
                />
            )}
        </div>
        <div className={cx("footer upload", {hidden: !state.value.showFooterUpload})}>
            <UploadButton
                buttonText="Upload a Photo"
                onUpload={onUpload}
                uploadProgressPercent={state.value.uploadProgressPercent}
                allowMultipleFiles={true}
            />
            <p>Images must be JPEG (.jpg, .jpeg, .jpe), PNG{disallowSvgImages ? "" : ", SVG"} or PDF format. Less than {Math.round(maxFileSize/1000000)}MB in file size.</p>
        </div>
        {multiSelectOptions && (
        <MultiSelectFooter
            maxSelections={multiSelectOptions.maxSelections}
            numSelected={multiSelectOptions.selectedImages.length}
            onApply={() => {
                state.onChange({...state.value, isImageLoadingToEditor: true});
                multiSelectOptions.onApply(imageArea, multiSelectOptions.selectedImages, modal.close);
            }}
            onClear={multiSelectOptions.onClearSelections}
        />
        )}
    </>))

    .run()

);